import { WEB_ICO } from './icons';
import { WEB_NAV_LIB, WEB_NAV_MAIN, type NavItem, type NavKey } from './nav';

function WebNavRow({
  item,
  active,
  collapsed,
  onClick,
  badge,
}: {
  item: NavItem;
  active: NavKey;
  collapsed: boolean;
  onClick: () => void;
  badge?: number | string | null;
}) {
  const on = active === item.key;
  return (
    <button
      onClick={onClick}
      title={item.label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: collapsed ? '11px 0' : '11px 12px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        background: on ? 'rgba(0,91,150,0.1)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-control)',
        cursor: 'pointer',
        fontFamily: 'var(--font-vi)',
        textAlign: 'left',
        color: on ? 'var(--cta-default)' : 'var(--text-secondary)',
      }}
    >
      <span style={{ display: 'flex', flexShrink: 0 }}>{WEB_ICO[item.ico](on ? 'var(--cta-default)' : 'var(--navy-300)', 20)}</span>
      {!collapsed && (
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5, fontWeight: on ? 700 : 600, color: on ? 'var(--cta-default)' : 'var(--ink)' }}>
            {item.label}
          </span>
          {item.hint && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 1 }}>{item.hint}</span>}
        </span>
      )}
      {!collapsed && badge != null && (
        <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#fff', background: 'var(--cta-default)', borderRadius: 999, padding: '2px 8px' }}>
          {badge}
        </span>
      )}
    </button>
  );
}

interface WebSidebarProps {
  active: NavKey;
  onNav: (key: NavKey) => void;
  /** Tên hiển thị của tài khoản; rỗng thì rơi về email, rồi về "Bạn". */
  name?: string | null;
  email?: string | null;
  aiEnabled?: boolean;
  socialEnabled?: boolean;
  collapsed?: boolean;
  due?: number;
  /** Tổng tin chưa đọc — badge mục Chat, ẩn khi 0. */
  chatUnread?: number;
}

/** Chữ cái đầu cho avatar — ưu tiên tên, sau đó email. */
function initial(name?: string | null, email?: string | null): string {
  const src = (name || email || '').trim();
  return src ? src[0]!.toUpperCase() : 'B';
}

export function WebSidebar({ active, onNav, name, email, aiEnabled = true, socialEnabled = true, collapsed = false, due = 0, chatUnread = 0 }: WebSidebarProps) {
  const main = WEB_NAV_MAIN.filter((i) => (i.key !== 'ai' || aiEnabled) && ((i.key !== 'congdong' && i.key !== 'chat') || socialEnabled));
  const displayName = name?.trim() || email || 'Bạn';
  return (
    <aside
      style={{
        width: collapsed ? 80 : 252,
        flexShrink: 0,
        background: 'var(--surface-page)',
        borderRight: '1px solid var(--border-default)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-vi)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '18px 0' : '18px 20px',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <img src="/assets/torii-mark.svg" alt="Torii" style={{ width: 30, height: 30 }} />
        {!collapsed && <span style={{ fontSize: 19, fontWeight: 700, letterSpacing: 1.5, color: 'var(--ink)' }}>TORII</span>}
      </div>

      <nav style={{ padding: '4px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {main.map((i) => (
          <WebNavRow
            key={i.key}
            item={i}
            active={active}
            collapsed={collapsed}
            badge={i.key === 'ontap' ? due : i.key === 'chat' && chatUnread > 0 ? (chatUnread > 99 ? '99+' : chatUnread) : null}
            onClick={() => onNav(i.key)}
          />
        ))}
      </nav>

      <div style={{ height: 1, background: 'var(--border-default)', margin: '14px 16px' }} />
      {!collapsed && (
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--navy-300)', padding: '0 20px 8px' }}>
          Thư viện
        </div>
      )}
      <nav style={{ padding: '0 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {WEB_NAV_LIB.map((i) => (
          <WebNavRow key={i.key} item={i} active={active} collapsed={collapsed} onClick={() => onNav(i.key)} />
        ))}
      </nav>

      <div style={{ flex: 1 }} />

      <div
        style={{
          padding: collapsed ? 12 : 16,
          borderTop: '1px solid var(--border-default)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            background: 'var(--navy-500)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          {initial(name, email)}
        </span>
        {!collapsed && (
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              title={displayName}
              style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {displayName}
            </span>
            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {email ?? ''}
            </span>
          </span>
        )}
        {!collapsed && (
          <button
            onClick={() => onNav('account')}
            aria-label="Cài đặt"
            title="Cài đặt"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid var(--border-default)',
              background: 'var(--surface-page)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {WEB_ICO.gear('var(--text-secondary)', 16)}
          </button>
        )}
      </div>
    </aside>
  );
}
